import type { HovSkillId } from "./hovSkillDefinitions";
import {
  EMPTY_HOV_SKILL_EFFECT_MODIFIERS,
  resolveHovSkillExternalMultiplier,
  type HovSkillEffectModifier,
} from "./hovSkillModifiers";
import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  maxHuge,
  type HugeNumber,
} from "@/shared/numbers";
import type { HovAmplifierEntryId } from "./hovAmplifierModel";

export {
  EMPTY_HOV_SKILL_EFFECT_MODIFIERS,
  resolveHovSkillExternalMultiplier,
  type HovSkillEffectModifier,
  type HovSkillModifierSource,
  type HovSkillModifierTarget,
} from "./hovSkillModifiers";

interface HovSkillBaseEffect {
  readonly id: HovSkillId;
  readonly hovGainPerSecond?: HugeNumber;
  readonly timGainPerSecond?: HugeNumber;
  readonly fairySpawnEnabled?: boolean;
  readonly mpCapacity?: HugeNumber;
  // [2, 1] bonus for BHG -> HG power exponent only; see ADR 0025.
  // Not an Amplifier / AP exponent.
  readonly bhgToHgPowerExponentBonus?: number;
  readonly amplifierUnlocked?: boolean;
  // Additive bonus to the base simultaneous purchase count for HG / HnG
  // Hov Gain Upgrade purchases. Composed inside the base BEFORE any Wall
  // reward multiplier so Price Wall still scales on top of it.
  readonly hgHngSimultaneousPurchaseCountBaseBonus?: number;
  readonly hov1GainMultiplier?: HugeNumber;
  // Entries whose source Amplifier Gain becomes eligible for the OHov-power
  // multiplier when this skill is acquired. Listed as entry ids so future
  // targets (orange / yellow / ...) can be added entry-by-entry through other
  // systems without changing the resolution shape.
  readonly amplifierGainOHovMultiplierTargetEntries?: readonly HovAmplifierEntryId[];
  readonly amplifierGainFixedMultipliersByEntry?: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  >;
  readonly amplifierGainClearedWallKindMultiplierTargetEntries?: readonly HovAmplifierEntryId[];
  readonly amplifierGainOHovRunElapsedMultiplierTargetEntries?: readonly HovAmplifierEntryId[];
  readonly amplifierPanelMpPlacementUnlocked?: boolean;
  readonly mhovMpExecutionUnlocked?: boolean;
  readonly nhovMpExecutionAssignmentUnlocked?: boolean;
  readonly ohovHovCoreGainMultiplierUnlocked?: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplierUnlocked?: boolean;
  readonly freeMpCoreOHovHovCoreGainMultiplierUnlocked?: boolean;
  // Additive bonus to the HnG rank scale base. The NHov hngScale branch
  // multiplier composes on top of (HOV_CELL_RANK_SCALE_BASE + sum of bonuses).
  readonly hngRankScaleBaseBonus?: number;
  // Additive bonus applied to the MHov and NHov1 / NHov3 / NHov4 branch
  // growth exponents. NHov2 stays the baseline branch and is never affected.
  readonly varietyPackGrowthExponentBonus?: number;
  readonly wallUnlocked?: boolean;
  readonly explorationUnlocked?: boolean;
  // Selects relaxed per-wall reward caps; it is not a shared reward multiplier.
  readonly wallRewardCapRelaxed?: boolean;
  // Fixed additive bonus to the effective Amplifier AP exponent.
  readonly apBoarExponentBonus?: number;
  // True when the AP Deer skill is acquired. Per-unlocked-amplifier-kind
  // bonus is resolved at the model boundary because it depends on dynamic
  // amplifier state.
  readonly apDeerExponentBonusAcquired?: boolean;
  // True when the AP Butterfly skill is acquired. AP-dependent bonus is
  // resolved at the model boundary because it depends on current AP.
  readonly apButterflyExponentBonusAcquired?: boolean;
}

export interface ResolveHovSkillEffectsOptions {
  readonly acquired: ReadonlySet<HovSkillId>;
  readonly externalModifiers?: readonly HovSkillEffectModifier[];
}

// Additive growth-exponent bonuses contributed by Hov skills, one per affected
// reset-layer branch. NHov2 (`/n HG コスト`) is intentionally absent: it stays
// the baseline branch with growth coefficient 1.
export interface MhovNHovGrowthExponentBonus {
  readonly mhov: number;
  // NHov1: `xN HG アップグレード`
  readonly nHovHgAmplify: number;
  // NHov3: `HG NHov xN HnG`
  readonly nHovHgNHovEffectsToHng: number;
  // NHov4: `HnG ランクスケール`
  readonly nHovHngScale: number;
}

export interface HovSkillResolvedEffects {
  readonly hovGainPerSecond: HugeNumber;
  readonly timGainPerSecond: HugeNumber;
  readonly fairySpawnEnabled: boolean;
  readonly mpCapacity: HugeNumber;
  readonly bhgToHgPowerExponentBonus: number;
  readonly amplifierUnlocked: boolean;
  // Sum of additive bonuses to the HG / HnG base simultaneous purchase count
  // from acquired Hov skills. Sits inside the base so Wall reward multipliers
  // still scale on top of it at the planner cap boundary.
  readonly hgHngSimultaneousPurchaseCountBaseBonus: number;
  readonly hov1GainMultiplier: HugeNumber;
  // Entries on which the OHov-power Amplifier Gain multiplier applies. Empty
  // when no skill currently selects any target.
  readonly amplifierGainOHovMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
  readonly amplifierGainFixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  >;
  readonly amplifierGainClearedWallKindMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
  readonly amplifierGainOHovRunElapsedMultiplierTargets: ReadonlySet<HovAmplifierEntryId>;
  // True when the player can assign MP to Amplifier Panels through MPM.
  readonly amplifierPanelMpPlacementUnlocked: boolean;
  readonly mhovMpExecutionUnlocked: boolean;
  readonly nhovMpExecutionAssignmentUnlocked: boolean;
  // True when the OHov HovCore reward multiplier skill is acquired. The final
  // multiplier value depends on whether the matching achievement is unlocked
  // and is resolved at the OHov reward boundary.
  readonly ohovHovCoreGainMultiplierUnlocked: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly freeMpCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  // Sum of additive bonuses to the HnG rank scale base from acquired Hov
  // skills. Consumed by HoverIdleModel when resolving the rank scale base.
  readonly hngRankScaleBaseBonus: number;
  // Additive growth-exponent bonuses for MHov / NHov1 / NHov3 / NHov4 from
  // acquired Hov skills. The single authoritative effect referenced by the
  // MHov and NHov snapshot, preview, and execution paths.
  readonly growthExponentBonus: MhovNHovGrowthExponentBonus;
  // True when the Wall tab becomes available to the player.
  readonly wallUnlocked: boolean;
  // True when Exploration is available to the player.
  readonly explorationUnlocked: boolean;
  // True when Wall 1-6 resolve their wall-owned relaxed effective caps.
  readonly wallRewardCapRelaxed: boolean;
  // Fixed additive bonus to the Amplifier AP exponent contributed by the
  // AP Boar skill. Composed with the base AP exponent and the dynamic
  // AP Deer / AP Butterfly bonuses at the model boundary.
  readonly apBoarExponentBonus: number;
  readonly apDeerExponentBonusAcquired: boolean;
  readonly apButterflyExponentBonusAcquired: boolean;
}

export const HOV_BHG_TO_HG_POWER_EXPONENT_SKILL_BONUS = 0.01;
export const HOV_MP_CAPACITY_SKILL_BONUS = 5;
export const HOV_MP_CAPACITY_SECOND_SKILL_BONUS = 10;
export const HOV_1_GAIN_MULTIPLIER_SKILL_BONUS = 10;
// Additive bonus contributed by the
// `hg-hng-simultaneous-purchase-count-base-bonus` skill to the HG / HnG base
// simultaneous purchase count. The Wall reward multiplier composes on top of
// (1 + sum of bonuses) at the planner cap boundary.
export const HOV_HG_HNG_SIMULTANEOUS_PURCHASE_COUNT_BASE_SKILL_BONUS = 1;
// Pre-achievement multiplier applied to OHov HovCore rewards once the
// `ohov-hov-core-gain-multiplier-bonus` skill is acquired. The achievement
// "burn-the-core" upgrades this to `max(BASE, OHovState.power)`.
export const OHOV_HOV_CORE_GAIN_MULTIPLIER_SKILL_BASE = 2;
// Additive bonus contributed to the HnG rank scale base by the
// `hng-rank-scale-base-bonus` skill. Sits inside the base, so the NHov
// hngScale branch multiplier still scales on top of it.
export const HOV_HNG_RANK_SCALE_BASE_SKILL_BONUS = 1;
// Additive growth-exponent bonus contributed by the `variety-pack` skill to
// MHov / NHov1 / NHov3 / NHov4. NHov2 stays the baseline branch.
export const HOV_VARIETY_PACK_GROWTH_EXPONENT_BONUS = 0.05;
export const HOV_RA_PLUM_AMPLIFIER_GAIN_MULTIPLIER = 5;

// Fixed additive bonus to the effective Amplifier AP exponent contributed
// by the AP Boar skill. Composed with AP Deer / AP Butterfly bonuses at
// the model boundary.
export const HOV_AP_BOAR_EXPONENT_BONUS = 0.1;
// Per-unlocked-amplifier-kind additive bonus for the AP Deer skill. The
// total bonus equals this constant times the unlocked amplifier kind count.
export const HOV_AP_DEER_EXPONENT_BONUS_PER_UNLOCKED_KIND = 0.02;
// AP Butterfly exponent parameter: bonus = max(MIN, AP^EXPONENT / DIVISOR).
export const HOV_AP_BUTTERFLY_EXPONENT_BONUS_AP_EXPONENT = 0.05;
export const HOV_AP_BUTTERFLY_EXPONENT_BONUS_DIVISOR = 100;
export const HOV_AP_BUTTERFLY_EXPONENT_BONUS_MIN = 0.01;

const HOV_SKILL_BASE_EFFECTS: readonly HovSkillBaseEffect[] = [
  {
    id: "amplifier-unlock",
    amplifierUnlocked: true,
  },
  {
    id: "hg-exponent-bonus",
    bhgToHgPowerExponentBonus: HOV_BHG_TO_HG_POWER_EXPONENT_SKILL_BONUS,
  },
  {
    id: "hg-hng-simultaneous-purchase-count-base-bonus",
    hgHngSimultaneousPurchaseCountBaseBonus:
      HOV_HG_HNG_SIMULTANEOUS_PURCHASE_COUNT_BASE_SKILL_BONUS,
  },
  {
    id: "mp-capacity-bonus",
    mpCapacity: huge(HOV_MP_CAPACITY_SKILL_BONUS),
  },
  {
    id: "hov-1-gain-multiplier-bonus",
    hov1GainMultiplier: huge(HOV_1_GAIN_MULTIPLIER_SKILL_BONUS),
  },
  {
    id: "amplifier-gain-ohov-multiplier-bonus",
    amplifierGainOHovMultiplierTargetEntries: ["red"],
  },
  {
    id: "ohov-hov-core-gain-multiplier-bonus",
    ohovHovCoreGainMultiplierUnlocked: true,
  },
  {
    id: "mhov-mp-control-unlock",
    mhovMpExecutionUnlocked: true,
  },
  {
    id: "amplifier-panel-mp-placement-unlock",
    amplifierPanelMpPlacementUnlocked: true,
  },
  {
    id: "hng-rank-scale-base-bonus",
    hngRankScaleBaseBonus: HOV_HNG_RANK_SCALE_BASE_SKILL_BONUS,
  },
  {
    id: "wall-unlock",
    wallUnlocked: true,
  },
  {
    id: "ra-plum-amplifier-gain-multiplier-bonus",
    amplifierGainFixedMultipliersByEntry: new Map([
      ["red", huge(HOV_RA_PLUM_AMPLIFIER_GAIN_MULTIPLIER)],
    ]),
  },
  {
    id: "ra-bamboo-amplifier-gain-multiplier-bonus",
    amplifierGainClearedWallKindMultiplierTargetEntries: ["red"],
  },
  {
    id: "ra-pine-amplifier-gain-multiplier-bonus",
    amplifierGainOHovRunElapsedMultiplierTargetEntries: ["red"],
  },
  {
    id: "ap-boar-exponent-bonus",
    apBoarExponentBonus: HOV_AP_BOAR_EXPONENT_BONUS,
  },
  {
    id: "ap-deer-exponent-bonus",
    apDeerExponentBonusAcquired: true,
  },
  {
    id: "ap-butterfly-exponent-bonus",
    apButterflyExponentBonusAcquired: true,
  },
  {
    id: "nhov-mp-control-unlock",
    nhovMpExecutionAssignmentUnlocked: true,
  },
  {
    id: "variety-pack",
    varietyPackGrowthExponentBonus: HOV_VARIETY_PACK_GROWTH_EXPONENT_BONUS,
  },
  {
    id: "mp-capacity-second-bonus",
    mpCapacity: huge(HOV_MP_CAPACITY_SECOND_SKILL_BONUS),
  },
  {
    id: "mp-capacity-core",
    mpCapacityCoreOHovHovCoreGainMultiplierUnlocked: true,
  },
  {
    id: "free-mp-core",
    freeMpCoreOHovHovCoreGainMultiplierUnlocked: true,
  },
  {
    id: "wall-cap-relaxation",
    wallRewardCapRelaxed: true,
  },
  {
    id: "exploration-unlock",
    explorationUnlocked: true,
  },
];

export function resolveHovSkillEffects(
  options: ResolveHovSkillEffectsOptions,
): HovSkillResolvedEffects {
  const externalModifiers =
    options.externalModifiers ?? EMPTY_HOV_SKILL_EFFECT_MODIFIERS;
  let hovGainPerSecond = hugeZero();
  let timGainPerSecond = hugeZero();
  let fairySpawnEnabled = false;
  let mpCapacity = hugeZero();
  let bhgToHgPowerExponentBonus = 0;
  let amplifierUnlocked = false;
  let hov1GainMultiplier = hugeOne();
  const amplifierGainOHovMultiplierTargets = new Set<HovAmplifierEntryId>();
  const amplifierGainFixedMultipliersByEntry = new Map<
    HovAmplifierEntryId,
    HugeNumber
  >();
  const amplifierGainClearedWallKindMultiplierTargets =
    new Set<HovAmplifierEntryId>();
  const amplifierGainOHovRunElapsedMultiplierTargets =
    new Set<HovAmplifierEntryId>();
  let amplifierPanelMpPlacementUnlocked = false;
  let mhovMpExecutionUnlocked = false;
  let nhovMpExecutionAssignmentUnlocked = false;
  let ohovHovCoreGainMultiplierUnlocked = false;
  let mpCapacityCoreOHovHovCoreGainMultiplierUnlocked = false;
  let freeMpCoreOHovHovCoreGainMultiplierUnlocked = false;
  let hngRankScaleBaseBonus = 0;
  let varietyPackGrowthExponentBonus = 0;
  let hgHngSimultaneousPurchaseCountBaseBonus = 0;
  let wallUnlocked = false;
  let explorationUnlocked = false;
  let wallRewardCapRelaxed = false;
  let apBoarExponentBonus = 0;
  let apDeerExponentBonusAcquired = false;
  let apButterflyExponentBonusAcquired = false;

  for (const effect of HOV_SKILL_BASE_EFFECTS) {
    if (!options.acquired.has(effect.id)) {
      continue;
    }

    const externalMultiplier = resolveHovSkillExternalMultiplier(
      effect.id,
      externalModifiers,
    );
    if (effect.hovGainPerSecond !== undefined) {
      hovGainPerSecond = hovGainPerSecond.add(
        effect.hovGainPerSecond.mul(externalMultiplier),
      );
    }
    if (effect.timGainPerSecond !== undefined) {
      timGainPerSecond = timGainPerSecond.add(
        effect.timGainPerSecond.mul(externalMultiplier),
      );
    }
    if (effect.fairySpawnEnabled === true) {
      fairySpawnEnabled = true;
    }
    if (effect.mpCapacity !== undefined) {
      mpCapacity = mpCapacity.add(effect.mpCapacity.mul(externalMultiplier));
    }
    if (effect.bhgToHgPowerExponentBonus !== undefined) {
      bhgToHgPowerExponentBonus += effect.bhgToHgPowerExponentBonus;
    }
    if (effect.amplifierUnlocked === true) {
      amplifierUnlocked = true;
    }
    if (effect.hov1GainMultiplier !== undefined) {
      hov1GainMultiplier = hov1GainMultiplier.mul(
        effect.hov1GainMultiplier.mul(externalMultiplier),
      );
    }
    if (effect.amplifierGainOHovMultiplierTargetEntries !== undefined) {
      for (const entryId of effect.amplifierGainOHovMultiplierTargetEntries) {
        amplifierGainOHovMultiplierTargets.add(entryId);
      }
    }
    if (effect.amplifierGainFixedMultipliersByEntry !== undefined) {
      for (const [
        entryId,
        multiplier,
      ] of effect.amplifierGainFixedMultipliersByEntry) {
        const current = amplifierGainFixedMultipliersByEntry.get(entryId);
        amplifierGainFixedMultipliersByEntry.set(
          entryId,
          (current ?? hugeOne()).mul(multiplier.mul(externalMultiplier)),
        );
      }
    }
    if (
      effect.amplifierGainClearedWallKindMultiplierTargetEntries !== undefined
    ) {
      for (const entryId of effect.amplifierGainClearedWallKindMultiplierTargetEntries) {
        amplifierGainClearedWallKindMultiplierTargets.add(entryId);
      }
    }
    if (
      effect.amplifierGainOHovRunElapsedMultiplierTargetEntries !== undefined
    ) {
      for (const entryId of effect.amplifierGainOHovRunElapsedMultiplierTargetEntries) {
        amplifierGainOHovRunElapsedMultiplierTargets.add(entryId);
      }
    }
    if (effect.amplifierPanelMpPlacementUnlocked === true) {
      amplifierPanelMpPlacementUnlocked = true;
    }
    if (effect.mhovMpExecutionUnlocked === true) {
      mhovMpExecutionUnlocked = true;
    }
    if (effect.nhovMpExecutionAssignmentUnlocked === true) {
      nhovMpExecutionAssignmentUnlocked = true;
    }
    if (effect.ohovHovCoreGainMultiplierUnlocked === true) {
      ohovHovCoreGainMultiplierUnlocked = true;
    }
    if (effect.mpCapacityCoreOHovHovCoreGainMultiplierUnlocked === true) {
      mpCapacityCoreOHovHovCoreGainMultiplierUnlocked = true;
    }
    if (effect.freeMpCoreOHovHovCoreGainMultiplierUnlocked === true) {
      freeMpCoreOHovHovCoreGainMultiplierUnlocked = true;
    }
    if (effect.hngRankScaleBaseBonus !== undefined) {
      hngRankScaleBaseBonus += effect.hngRankScaleBaseBonus;
    }
    if (effect.varietyPackGrowthExponentBonus !== undefined) {
      varietyPackGrowthExponentBonus += effect.varietyPackGrowthExponentBonus;
    }
    if (effect.hgHngSimultaneousPurchaseCountBaseBonus !== undefined) {
      hgHngSimultaneousPurchaseCountBaseBonus +=
        effect.hgHngSimultaneousPurchaseCountBaseBonus;
    }
    if (effect.wallUnlocked === true) {
      wallUnlocked = true;
    }
    if (effect.explorationUnlocked === true) {
      explorationUnlocked = true;
    }
    if (effect.wallRewardCapRelaxed === true) {
      wallRewardCapRelaxed = true;
    }
    if (effect.apBoarExponentBonus !== undefined) {
      apBoarExponentBonus += effect.apBoarExponentBonus;
    }
    if (effect.apDeerExponentBonusAcquired === true) {
      apDeerExponentBonusAcquired = true;
    }
    if (effect.apButterflyExponentBonusAcquired === true) {
      apButterflyExponentBonusAcquired = true;
    }
  }

  return {
    hovGainPerSecond,
    timGainPerSecond,
    fairySpawnEnabled,
    mpCapacity,
    bhgToHgPowerExponentBonus,
    amplifierUnlocked,
    hov1GainMultiplier,
    amplifierGainOHovMultiplierTargets,
    amplifierGainFixedMultipliersByEntry,
    amplifierGainClearedWallKindMultiplierTargets,
    amplifierGainOHovRunElapsedMultiplierTargets,
    amplifierPanelMpPlacementUnlocked,
    mhovMpExecutionUnlocked,
    nhovMpExecutionAssignmentUnlocked,
    ohovHovCoreGainMultiplierUnlocked,
    mpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
    freeMpCoreOHovHovCoreGainMultiplierUnlocked,
    hngRankScaleBaseBonus,
    growthExponentBonus: {
      mhov: varietyPackGrowthExponentBonus,
      nHovHgAmplify: varietyPackGrowthExponentBonus,
      nHovHgNHovEffectsToHng: varietyPackGrowthExponentBonus,
      nHovHngScale: varietyPackGrowthExponentBonus,
    },
    hgHngSimultaneousPurchaseCountBaseBonus,
    wallUnlocked,
    explorationUnlocked,
    wallRewardCapRelaxed,
    apBoarExponentBonus,
    apDeerExponentBonusAcquired,
    apButterflyExponentBonusAcquired,
  };
}

export function resolveApDeerExponentBonus(
  unlockedAmplifierKindCount: number,
): number {
  if (
    !Number.isFinite(unlockedAmplifierKindCount) ||
    unlockedAmplifierKindCount <= 0
  ) {
    return 0;
  }
  return (
    HOV_AP_DEER_EXPONENT_BONUS_PER_UNLOCKED_KIND * unlockedAmplifierKindCount
  );
}

export function resolveApButterflyExponentBonus(ap: HugeNumber): number {
  if (!ap.isFinite() || compareHuge(ap, 0) <= 0) {
    return HOV_AP_BUTTERFLY_EXPONENT_BONUS_MIN;
  }
  const computed = ap
    .pow(HOV_AP_BUTTERFLY_EXPONENT_BONUS_AP_EXPONENT)
    .div(HOV_AP_BUTTERFLY_EXPONENT_BONUS_DIVISOR)
    .toNumber();
  if (!Number.isFinite(computed)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Math.max(HOV_AP_BUTTERFLY_EXPONENT_BONUS_MIN, computed);
}

export interface ResolveApExponentBonusBreakdownOptions {
  readonly apBoarExponentBonus: number;
  readonly apDeerExponentBonusAcquired: boolean;
  readonly apButterflyExponentBonusAcquired: boolean;
  readonly unlockedAmplifierKindCount: number;
  readonly ap: HugeNumber;
}

export interface ApExponentBonusBreakdown {
  readonly apBoarExponentBonus: number;
  readonly apDeerExponentBonus: number;
  readonly apButterflyExponentBonus: number;
  readonly total: number;
}

export function resolveApExponentBonusBreakdown(
  options: ResolveApExponentBonusBreakdownOptions,
): ApExponentBonusBreakdown {
  const apDeerExponentBonus = options.apDeerExponentBonusAcquired
    ? resolveApDeerExponentBonus(options.unlockedAmplifierKindCount)
    : 0;
  const apButterflyExponentBonus = options.apButterflyExponentBonusAcquired
    ? resolveApButterflyExponentBonus(options.ap)
    : 0;
  return {
    apBoarExponentBonus: options.apBoarExponentBonus,
    apDeerExponentBonus,
    apButterflyExponentBonus,
    total:
      options.apBoarExponentBonus +
      apDeerExponentBonus +
      apButterflyExponentBonus,
  };
}

export interface ResolveOHovHovCoreGainMultiplierOptions {
  readonly skillAcquired: boolean;
  readonly achievementScalingUnlocked: boolean;
  readonly oHovPower: HugeNumber;
  readonly mpCapacityCoreAcquired?: boolean;
  readonly freeMpCoreAcquired?: boolean;
  readonly mpCapacity?: number;
  readonly freeMp?: number;
}

export interface ResolveOHovHovCoreGainPostAcquisitionMultiplierOptions {
  readonly achievementScalingUnlocked: boolean;
  readonly oHovPower: HugeNumber;
}

export function resolveOHovHovCoreGainPostAcquisitionMultiplier(
  options: ResolveOHovHovCoreGainPostAcquisitionMultiplierOptions,
): HugeNumber {
  const base = huge(OHOV_HOV_CORE_GAIN_MULTIPLIER_SKILL_BASE);
  if (!options.achievementScalingUnlocked) {
    return base;
  }
  if (compareHuge(options.oHovPower, base) >= 0) {
    return maxHuge(base, options.oHovPower);
  }
  return base;
}

export function resolveMpCapacityCoreOHovHovCoreGainMultiplier(options: {
  readonly mpCapacity: number;
}): HugeNumber {
  return huge(toNonNegativeInteger(options.mpCapacity));
}

export function resolveFreeMpCoreOHovHovCoreGainMultiplier(options: {
  readonly freeMp: number;
}): HugeNumber {
  return huge(1 + toNonNegativeInteger(options.freeMp));
}

// Centralized formula for the OHov HovCore reward multiplier so balance
// changes stay in one place. Returns the multiplier applied to the base
// HovCore gain at OHov execution time.
//
// - Older OHov HovCore skill locked -> x1
// - Older skill acquired, achievement locked -> xBASE
// - Older skill acquired, achievement unlocked -> xmax(BASE, OHovState.power)
// - MP Capacity Core acquired -> multiply by current MP capacity
// - Free MP Core acquired -> multiply by 1 + current free MP
export function resolveOHovHovCoreGainMultiplier(
  options: ResolveOHovHovCoreGainMultiplierOptions,
): HugeNumber {
  let multiplier = options.skillAcquired
    ? resolveOHovHovCoreGainPostAcquisitionMultiplier(options)
    : hugeOne();

  if (options.mpCapacityCoreAcquired === true) {
    multiplier = multiplier.mul(
      resolveMpCapacityCoreOHovHovCoreGainMultiplier({
        mpCapacity: options.mpCapacity ?? 0,
      }),
    );
  }

  if (options.freeMpCoreAcquired === true) {
    multiplier = multiplier.mul(
      resolveFreeMpCoreOHovHovCoreGainMultiplier({
        freeMp: options.freeMp ?? 0,
      }),
    );
  }

  return multiplier;
}

function toNonNegativeInteger(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

export function areAllRow2HovSkillsAcquired(
  acquired: ReadonlySet<HovSkillId>,
): boolean {
  return acquired.has("hg-exponent-bonus") && acquired.has("mp-capacity-bonus");
}
